import { buildSearchText, getVerificationStatus } from "@nomnom/core";
import type {
  RiderBackgroundCheckStatus,
  RiderProfile,
  User,
  VerificationStatus,
} from "@nomnom/core";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

export type RiderRow = {
  id: string;
  profile: RiderProfile;
  user: User | null;
  verificationStatus: VerificationStatus;
  displayName: string;
  email: string;
  phoneNumber: string;
};

export type RiderStats = {
  totalRiders: number;
  verifiedRiders: number;
  onlineRiders: number;
  avgRating: number;
};

export function subscribeToRiders(
  onUpdate: (riders: RiderRow[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    query(collection(db, "riderProfiles"), orderBy("createdAt", "desc")),
    (snapshot) => {
      const loadRiders = async () => {
        try {
          const profiles = snapshot.docs.map(
            (docSnap) =>
              ({ uid: docSnap.id, ...docSnap.data() }) as RiderProfile,
          );

          const userSnaps = await Promise.all(
            profiles.map((profile) => getDoc(doc(db, "users", profile.uid))),
          );

          const userMap = new Map<string, User>();
          userSnaps.forEach((userSnap) => {
            if (userSnap.exists()) {
              const userData = userSnap.data() as Omit<User, "uid">;
              userMap.set(userSnap.id, { uid: userSnap.id, ...userData });
            }
          });

          onUpdate(
            profiles.map((profile) => {
              const user = userMap.get(profile.uid) || null;
              const verificationStatus = getVerificationStatus(profile);

              return {
                id: profile.uid,
                profile,
                user,
                verificationStatus,
                displayName:
                  user?.displayName ||
                  profile.ucsdEmail?.split("@")[0] ||
                  "Rider",
                email: profile.ucsdEmail || user?.email || "Not provided",
                phoneNumber: user?.phoneNumber || "Not provided",
              };
            }),
          );
        } catch (error) {
          console.error("[RidersService] Error loading riders:", error);
          onError("Failed to load rider profiles.");
        }
      };

      void loadRiders();
    },
    (error) => {
      console.error("[RidersService] Snapshot error:", error);
      onError("Failed to load rider profiles.");
    },
  );
}

export function filterRiders(
  riders: RiderRow[],
  filter: string,
  search: string,
): RiderRow[] {
  const searchTerm = search.toLowerCase();

  return riders.filter((rider) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "online" && rider.profile.online) ||
      (filter === "offline" && !rider.profile.online) ||
      (filter === "verified" && rider.verificationStatus === "approved") ||
      (filter === "pending" && rider.verificationStatus === "pending") ||
      (filter === "rejected" && rider.verificationStatus === "rejected");

    const matchesSearch = buildSearchText([
      rider.displayName,
      rider.email,
      rider.phoneNumber,
    ]).includes(searchTerm);

    return matchesFilter && matchesSearch;
  });
}

export function buildRiderStats(riders: RiderRow[]): RiderStats {
  const verifiedRiders = riders.filter(
    (rider) => rider.verificationStatus === "approved",
  ).length;
  const onlineRiders = riders.filter((rider) => rider.profile.online).length;
  const ratingValues = riders
    .map((rider) => rider.profile.ratingAvg)
    .filter((rating) => rating > 0);
  const avgRating =
    ratingValues.length > 0
      ? ratingValues.reduce((sum, rating) => sum + rating, 0) /
        ratingValues.length
      : 0;

  return {
    totalRiders: riders.length,
    verifiedRiders,
    onlineRiders,
    avgRating,
  };
}

export async function updateRiderVerification(
  riderId: string,
  actionStatus: VerificationStatus,
  actionNotes: string,
  options?: {
    backgroundCheckStatus?: RiderBackgroundCheckStatus;
    payoutEligible?: boolean;
    payoutHoldReason?: string;
    safetyHoldActive?: boolean;
    safetyHoldReason?: string;
    w9Status?: "missing" | "submitted" | "approved" | "rejected";
  },
): Promise<void> {
  try {
    await updateDoc(doc(db, "riderProfiles", riderId), {
      verified: actionStatus === "approved",
      verificationStatus: actionStatus,
      verificationNotes: actionNotes.trim() || null,
      verificationUpdatedAt: Timestamp.now(),
      backgroundCheckStatus: options?.backgroundCheckStatus,
      backgroundCheckCompletedAt:
        options?.backgroundCheckStatus === "cleared" ||
        options?.backgroundCheckStatus === "failed"
          ? Timestamp.now()
          : null,
      payoutEligible: options?.payoutEligible,
      payoutHoldReason: options?.payoutEligible
        ? null
        : options?.payoutHoldReason?.trim() || null,
      safetyHoldActive: options?.safetyHoldActive === true,
      safetyHoldReason: options?.safetyHoldActive
        ? options?.safetyHoldReason?.trim() || "Manual safety hold"
        : null,
      safetyHoldReportedAt: options?.safetyHoldActive ? Timestamp.now() : null,
      w9Status: options?.w9Status,
      w9ReceivedAt:
        options?.w9Status === "approved" || options?.w9Status === "submitted"
          ? Timestamp.now()
          : null,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("[RidersService] Error updating rider:", error);
    throw new Error("Failed to update rider verification.");
  }
}
